import { CenterInfo } from "./center.model";

export interface User {
  info: UserInfo;
  token: Token;
}

export interface Token {
  access_token: string;
  token_type: string;
}

export enum Role {
  admin = "A",
  hcp = "H",
  patient = "P",
  caregiver = "C",
  internal = "I",
}

export interface UserInfo extends WithPivot {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
  email_verified_at?: Date;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  role_id: number;
  role_in_department: string;
  is_admin: boolean;
  is_internal: boolean;
  is_password_expired: boolean;
  password_updated_at?: Date;
  is_contentmanager: boolean;
  role: RoleInfo;
  preference?: {
    language_code?: string;
  };
  patients?: PatientInfo[];
  zone_id?: number;
  zone_name?: string;
  assignment_strategy?: "by_center" | "by_hcp" | "self_registration";
  invitation_id?: number;
  has_requested_erasure?: boolean;
}

export interface RoleBaseInfo {
  name: string;
  type: Role;
  zone_id?: number;
}

export interface RoleInfo extends RoleBaseInfo {
  id: number;
  created_at?: Date;
  updated_at?: Date;
  centers?: CenterWithPermission[];
  is_custom_role?: boolean;
}

export interface PatientInfo extends WithPivot {
  id?: string;
  email?: string;
  firstname: string;
  lastname: string;
  birthdate?: string;
  sex: Gender;
  address?: string;
  zipcode?: string;
  city?: string;
  region?: string;
  country?: string;
  phone_number?: string;
  center_id: number;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  right_to_erasure_requested_at?: Date;
  users?: UserInfo[];
  self_registration?: boolean;
}

export interface WithPivot {
  pivot?: Pivot;
}
export interface Pivot {
  user_id: string;
  patient_id: string;
}

export enum Gender {
  male = "1",
  female = "2",
  other = "3",
  transexual = "4",
  unknown = "5",
}

export interface RolePermission {
  role_id: number;
  center_id: number;
  // patient list available
  index: boolean;
  // patient detail available
  show: boolean;
  // patient create available
  store: boolean;
  // patient update available
  update: boolean;
  // patient delete available
  destroy: boolean;
  created_at?: Date;
  updated_at?: Date;
}

export interface CenterWithPermission extends CenterInfo {
  pivot: RolePermission;
}

export interface CreatePatientInfo extends PatientInfo {
  caregiver_account?: boolean;
  caregiver_firstname?: string;
  caregiver_lastname?: string;
  caregiver_email?: string;
  caregiver_phone_number?: string;
  send_mail: boolean;
}

export function newPatientInfo(): CreatePatientInfo {
  return {
    firstname: "",
    lastname: "",
    sex: Gender.male,
    birthdate: "",
    address: "",
    city: "",
    country: "",
    center_id: -1,
    caregiver_account: false,
    send_mail: true,
  } as CreatePatientInfo;
}

export interface CreateUserInfo {
  id?: string;
  firstname?: string;
  lastname?: string;
  email: string;
  phone_number: string;
  role_id: number;
  role_in_department?: string;
  password?: string;
}

export interface UserStats {
  patients_total: number;
  patients_with_caregiver: number;
  center: CenterInfo;
}

export interface ResetPasswordRequest {
  scope: ResetPasswordAuthFactor;
  identifier: string;
}
export interface ResetPassword {
  scope: ResetPasswordAuthFactor;
  identifier: string;
  code: string;
  password: string;
  password_confirmation: string;
}

export enum ResetPasswordAuthFactor {
  email = "reset_pwd_via_email",
  phone = "reset_pwd_via_phone",
}

export interface UpdatePassword {
  password: string;
  password_confirmation: string;
  current_password: string;
}

export interface RoleWithCenterPermissions extends RoleInfo {
  center_permissions: RolePermission;
}

export interface PatientExtraDefinition {
  id: number;
  type:
    | "textarea"
    | "select"
    | "radio"
    | "text"
    | "number"
    | "range"
    | "checkbox"
    | "date"
    | "select-text";
  name: string;
  label: string;
  description?: string;
  rules?: {
    min?: number;
    max?: number;
    required?: boolean;
    is_filled?: string;
    list?: boolean;
  };
  default_value: number | string;
  options?: {
    text: string;
    value: number | string;
  }[];
  value: string | number;
  visible: boolean;
}

export interface PatientExtras {
  schema_name: string;
  description: string;
  definitions: PatientExtraDefinition[];
}

export interface PatientExtraDefinitionValue {
  dataextra_schema_name: string;
  dataextra_value: {
    [key: string]: string | number | ExtraResponseTextSelect;
  };
}

export interface PatientSearchData {
  firstname: string;
  lastname: string;
  id: string;
}

export type PatientExtraResponses = {
  [key: string]: {
    [key: string]: string | number | ExtraResponseTextSelect | any;
  };
};
export type ExtraResponseTextSelect = { [select: string]: string };
